/**
 * FASE 4 — RELATÓRIO DE COMPLETUDE DO CADASTRO.
 *
 * Responde "o que falta preencher": para cada dado, quantos atletas estão sem
 * ele e QUEM são (com a equipe). Clicar no nome abre a ficha do atleta para
 * editar — o relatório é para AÇÃO, não só para número.
 *
 * Quem vê o quê: é a MESMA lista que o usuário enxerga na tela de atletas
 * (professor vê os atletas dele; admin/federação veem todos) — o filtro é do
 * servidor.
 */
import { Fragment, useCallback, useEffect, useState } from 'react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { AlertTriangle, CheckCircle2, ChevronDown, Loader2, RefreshCw } from 'lucide-react';
import { athleteApi } from '../lib/api';

export default function CompletudeCadastro({ onAbrirAtleta }) {
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [aberto, setAberto] = useState(null);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const { data } = await athleteApi.completude();
      setDados(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao carregar o relatório');
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  if (carregando) {
    return (
      <div className="flex items-center justify-center py-10">
        <Loader2 className="w-6 h-6 text-[#D4AF37] animate-spin" aria-hidden="true" /><span role="status" aria-live="polite" className="ml-2 text-sm text-[#A1A1AA]">Carregando...</span>
      </div>
    );
  }

  if (!dados) {
    return <p className="text-sm text-[#A1A1AA]">Não foi possível carregar o relatório.</p>;
  }

  const pct = (n, total) => (total ? Math.round((n / total) * 100) : 0);

  return (
    <div className="space-y-4" data-testid="completude-relatorio">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-[#A1A1AA]">
          <strong className="text-white">{dados.total}</strong> atleta(s) na sua visão.
          A lista mostra <strong className="text-white">quem</strong> está sem cada dado.
        </p>
        <Button
          variant="outline"
          size="sm"
          onClick={carregar}
          data-testid="completude-recarregar"
          className="border-[#27272A] text-[#A1A1AA] hover:text-white"
        >
          <RefreshCw className="w-3.5 h-3.5 mr-1" /> Atualizar
        </Button>
      </div>

      <div className="rounded-lg border border-[#27272A] overflow-hidden">
        <table className="tabela w-full text-sm">
          <thead className="bg-[#1A1A1A]">
            <tr className="text-micro uppercase tracking-wider text-[#71717A]">
              <th className="text-left px-3 py-2">Dado</th>
              <th className="text-right px-3 py-2">Sem preencher</th>
              <th className="text-right px-3 py-2">Preenchido</th>
              <th className="text-right px-3 py-2">% ok</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {dados.campos.map((c) => {
              const expandido = aberto === c.campo;
              const critico = c.faltando > 0;
              return (
                <Fragment key={c.campo}>
                  <tr
                    data-testid={`completude-linha-${c.campo}`}
                    className="border-t border-[#27272A] hover:bg-[#141414]"
                  >
                    <td className="px-3 py-2 text-white">{c.rotulo}</td>
                    <td className={`px-3 py-2 text-right font-semibold ${critico ? 'text-amber-400' : 'text-emerald-400'}`}>
                      {c.faltando}
                    </td>
                    <td className="px-3 py-2 text-right text-[#A1A1AA]">{c.completo}</td>
                    <td className="px-3 py-2 text-right text-[#A1A1AA]">{pct(c.completo, c.total)}%</td>
                    <td className="px-3 py-2 text-right">
                      {c.faltando > 0 ? (
                        <button
                          type="button"
                          onClick={() => setAberto(expandido ? null : c.campo)}
                          data-testid={`completude-ver-${c.campo}`}
                          className="text-micro text-[#D4AF37] underline hover:text-white inline-flex items-center gap-1"
                        >
                          ver quem
                          <ChevronDown className={`w-3 h-3 transition-transform ${expandido ? 'rotate-180' : ''}`} />
                        </button>
                      ) : (
                        <span className="text-micro text-emerald-400 inline-flex items-center gap-1">
                          <CheckCircle2 className="w-3 h-3" /> completo
                        </span>
                      )}
                    </td>
                  </tr>
                  {expandido && (
                    <tr className="border-t border-[#27272A] bg-[#0F0F0F]">
                      <td colSpan={5} className="px-3 py-2">
                        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 max-h-64 overflow-auto">
                          {c.atletas.map((a) => (
                            <li key={a.id} className="text-xs flex items-center gap-2">
                              <button
                                type="button"
                                onClick={() => onAbrirAtleta && onAbrirAtleta(a)}
                                className="text-left text-white hover:text-[#D4AF37] underline decoration-dotted truncate"
                              >
                                {a.name}
                              </button>
                              <span className="text-micro text-[#71717A] truncate">
                                {a.belt}{a.team_name ? ` · ${a.team_name}` : ''}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      <p className="text-micro text-[#71717A] flex items-start gap-1">
        <AlertTriangle className="w-3 h-3 shrink-0 mt-0.5" />
        Nada aqui bloqueia o atleta: são dados que faltam para a ficha ficar completa.
        Clique no nome para abrir a ficha e preencher.
      </p>
    </div>
  );
}
